import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import * as pdfjsLib from "pdfjs-dist";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class CountpagesService {
  constructor(private translateService: TranslateService) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = "assets/vendor/pdf.worker.js";
    // pdfjsLib.PDFJS.workerSrc = '';
  }

  getPageCount(blob64: any, name: string, idx: number): Observable<number> {
    const type = name.substr(name.lastIndexOf(".")).toLowerCase();
    const sign = this.getSignature(blob64 || "", type);
    switch (sign) {
      case ".pdf": {
        return this.getPdfPageCount(blob64, idx);
      }
      case ".xls":
      case ".doc":
      case ".ppt":
      case ".xlsx":
      case ".docx":
      case ".pptx":
      case ".odt": {
        return this.getOfficePageCount(blob64, type, idx);
      }
      case ".rtf": {
        return this.getRichTextPageCount(blob64);
      }
      //case '.txt': {
      //  return this.getTextPageCount(blob64);
      //}
      case ".jpg":
      case ".jpeg":
      case ".png":
      case ". gif":
      case ".tif":
      case ".tiff":
      case ".bmp": {
        return this.getImagePageCount();
      }
      default: {
        return this.getNoPageCount();
      }
    }
  }

  getSignature(blob64: any, type: string): string {
    const first8 = blob64.slice(0, 8);
    switch (first8) {
      case "JVBERi0x":
        return ".pdf";
      case "e1xydGYx":
        return ".rtf";
      case "/9j/4AAQ":
        return ".jpg";
      case "iVBORw0K":
        return ".png";
      case "R0lGODlh":
        return ".gif";
      case "SUkqAIy8":
        return ".tif";
      case "Qk2iBFwA":
        return ".bmp";
    }
    return type;
  }

  getPdfPageCount(blob64: any, idx: number): Observable<number> {
    return new Observable((observer) => {
      const pdfData = atob(blob64);
      pdfjsLib.getDocument({ data: pdfData }).promise.then(
        (pdf) => {
          observer.next(pdf.numPages);
          observer.complete();
          pdf.destroy();
        },
        (error) => {
          observer.error([error, idx]);
        }
      );
    });
  }

  getOfficePageCount(
    blob64: any,
    type: string,
    idx: number
  ): Observable<number> {
    return new Observable((observer) => {
      try {
        if (Worker) {
          // bug 3602 fix: page count task freezes UI
          // to fix it, page counting logic for office documents
          // was moved to a Web Worker (inside cfb.js)
          // the worker receives a message with these parameters:
          // ['pagecount', fileContentBase64, fileType]
          // the Worker returns a message with these parameters:
          // ['enc'|'xml'|'cfb'|'unk',null|xml|pageCount]
          // enc => document is encrypted
          // xml => second parameter contains office metadata in XML form (can not
          // be read by the web worker because web workers do not have a DOM parser)
          // cfb => second parameter contains an integer with the page count
          // unk => worker could not retrieve page count
          const cfbWorker = new Worker("assets/vendor/cfb.js");
          cfbWorker.postMessage(["pagecount", blob64, type]);
          const that = this;
          cfbWorker.onmessage = (e) => {
            const msg = e.data[0];
            switch (msg) {
              case "enc":
                observer.error([
                  this.translateService.instant("ENCRYPTION_INFO_FOUND"),
                  idx,
                ]);
                break;
              case "xml":
                {
                  const xml = String.fromCharCode.apply(null, e.data[1]);
                  observer.next(that.readFromOfficeMetadata(xml));
                }
                break;
              case "cfb":
                observer.next(e.data[1]);
                break;
              default:
                observer.next(0);
            }
          };
          cfbWorker.onerror = (e) => {
            observer.error([e.message, idx]);
          };
        } else {
          // browser does not have Web Worker, sin loi...
          observer.next(0);
        }
      } catch (error) {
        observer.error([error, idx]);
      }
    });
  }

  readFromOfficeMetadata(xml: string): number {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, "text/xml");
    // is it a Word document?
    const docPages = xmlDoc.getElementsByTagName("Pages");
    if (docPages && docPages.length) {
      return parseInt(docPages[0].textContent, 10);
    }
    // is it a Open Document?
    const metaDoc = xmlDoc.getElementsByTagName("meta:document-statistic");
    if (metaDoc && metaDoc.length) {
      return parseInt(metaDoc[0].getAttribute("meta:page-count"), 10);
    }
    // is it a PowerPoint presentation?
    const pptSlides = xmlDoc.getElementsByTagName("Slides");
    if (pptSlides && pptSlides.length) {
      return parseInt(pptSlides[0].textContent, 10);
    }
    // so, should be an Excel spreadsheet. Read from property bags...
    //const bag = xmlDoc.getElementsByTagName('vt:variant');
    const bag = xmlDoc.getElementsByTagNameNS("*", "variant");
    //const bag = xmlDoc.evaluate('//vt:variant', xmlDoc, this.nsResolver, XPathResult.ANY_UNORDERED_NODE_TYPE, null);
    for (let i = 0; i < bag.length; ++i) {
      if (bag[i].textContent === "Worksheets") {
        return parseInt(bag[i + 1].textContent, 10);
      }
    }
    // what the heck...
    return 0;
  }

  // private nsResolver: XPathNSResolver = {
  //   lookupNamespaceURI: (prefix) => {
  //     if (prefix === 'vt') {
  //       return 'http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes';
  //     } else {
  //       return null;
  //     }
  //   },
  // };

  getRichTextPageCount(blob64: any): Observable<number> {
    return new Observable((observer) => {
      const text = atob(blob64);
      let count = 1;
      const regex = /{\\nofpages(\d+)}/g;
      const match = regex.exec(text);
      if (match) {
        count = parseInt(match[1], 10);
      }
      observer.next(count);
    });
  }

  getTextPageCount(blob64: any): Observable<number> {
    return new Observable((observer) => {
      const text = atob(blob64);
      const count = text.split(/\r\n|\r|\n/).length;
      observer.next(Math.ceil(count / 72));
    });
  }

  getImagePageCount(): Observable<number> {
    return new Observable((observer) => {
      observer.next(1);
    });
  }

  getNoPageCount(): Observable<number> {
    return new Observable((observer) => {
      observer.next(0);
    });
  }
}
